import React, { useState } from 'react';
import './StackMenu.css';
import { DomainType, WidgetFormat } from '@/Shared/Types';
import { GenericDomainWidget } from '../../Widgets';
import { useFadeIn } from '@/Shared/Logic';

export type StackItem = {
	type: DomainType,
	label?: string;
};

type StackMenuProps = {
	defaultItem?: StackItem;
	allowAll: boolean;
	format: WidgetFormat;
	stackItems: StackItem[]
};


/**
 * StackMenu component
 *
 * Tab-based domain navigation component used to switch between
 * multiple domain widgets without leaving the current page.
 *
 * Each tab represents a domain (Activity, Property, Contact, etc.)
 * and dynamically renders the corresponding GenericDomainWidget.
 *
 * Design considerations:
 * - Domain switching is handled locally via internal state
 * - Content transitions are visually smoothed using `useFadeIn`
 * - Component remounting is enforced to comply with React hook rules
 *
 * Author: Samano CASTRE
 * Date: January 2026
 */
const StackMenu: React.FC<StackMenuProps> = ({
	defaultItem,
	format,
	allowAll,
	stackItems
}) => {


	/**
	 * Currently active domain item.
	 * Defaults to the provided defaultItem or the first stack item.
	 */
	const [activeItem, setActiveItem] = useState<StackItem>(
		defaultItem ?? stackItems[0]
	);

	/**
	 * Apply a subtle fade-in animation when switching domains
	 */
	const fadeInClass = useFadeIn(300, activeItem.type);

	return (
		<div className="StackMenu" data-testid="StackMenu">

			{/* Stack navigation tabs */}
			<div className="stack-menu">
				{stackItems.map(item => (
					<button
						key={item.type}
						className={`stack-tab ${item.type === activeItem.type ? 'active' : ''}`}
						onClick={() => setActiveItem(item)}
					>
						{item.label ?? item.type}
					</button>
				))}
			</div>

			{/* Stack content area */}
			<div className='stack-body'>
				<div className={`stack-content ${fadeInClass}`}>
					{/* 
					 React Hooks Rule Compliance

					GenericDomainWidget dynamically resolves a domain-specific hook
					(useActivityTableConfig, useMatchingTableConfig, etc.).

					Since these hooks do not share the same internal hook structure,
					React MUST remount the component when switching domains.

					The `key={activeItem.type}` ensures a full unmount/remount cycle,
					preventing hook order violations and undefined behavior.
					*/}
					<GenericDomainWidget
						key={activeItem.type}
						allowAll={allowAll}
						format={format}
						domainType={activeItem.type}
					/>
				</div>
			</div>

		</div>
	);
};


export default StackMenu;
